import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CLIENT_SUPPORT_HUB_PATH } from './supportHubPaths';
import './locations.css';
import kkFavicon from './images/kk-favicon.png';
import checkWarning from './images/check-warning.png';
import appIcon from './images/icons/app-icon.svg';
import assistantIcon from './images/icons/assistant-icon.svg';
import osIcon from './images/icons/os-icon.svg';
import locationsIcon from './images/icons/locations-icon.svg';
import editIcon from './images/icons/edit-icon.svg';
import closeIcon from './images/icons/close-icon.svg';
import infoIcon from './images/icons/info-circle-icon.svg';
import USMap from './components/USMap';
import LocationCreateModal from './components/LocationCreateModal';
import { useBrand } from './context/BrandContext';
import { useOptionalTenant } from './tenant/TenantContext';
import apiService from './services/api';

const Locations = () => {
  const navigate = useNavigate();
  const { currentBrand } = useBrand();
  const tenantCtx = useOptionalTenant();
  const effectiveRole = String(tenantCtx?.tenant?.effectiveRole || '').toLowerCase();
  const canCreateLocation = effectiveRole === 'owner' || effectiveRole === 'admin';
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editableValues, setEditableValues] = useState({
    name: '',
    address: '',
    timeZone: '',
    region: '',
  });
  const [hasChanges, setHasChanges] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  
  // Map canonical GET /me/locations rows into the existing Locations table shape.
  // Status column uses Good/Check (playback health), not Active/Inactive.
  // Fleet last-played telemetry is not wired yet — live Locations default to
  // Good + "Played within 24 hrs"; inactive/pending → Check + warning icon.
  const mapCanonicalLocation = (row) => {
    const statusRaw = String(row?.status || row?.assignmentStatus || 'active').toLowerCase();
    const isCheck =
      statusRaw === 'inactive' ||
      statusRaw === 'pending-review' ||
      statusRaw === 'pending';
    const status = isCheck ? 'Check' : 'Good';
    const address =
      row?.address ||
      [
        row?.address_street,
        row?.address_street2,
        row?.address_city,
        row?.address_state,
        row?.address_zipCode,
      ]
        .filter(Boolean)
        .join(', ') ||
      '';
    return {
      id: row.locationId,
      name: row.locationName || 'Location',
      address: address.includes('\n') ? address : address.replace(/, /g, '\n'),
      timeZone: row.timezone || '—',
      region: '—',
      status,
      lastPlayed: isCheck ? 'Not played within 24 hrs' : 'Played within 24 hrs',
      icon: isCheck ? checkWarning : kkFavicon,
      brandId: row.brandId || null,
      assignmentStatus: row.assignmentStatus || null,
      storeNumber: row.storeNumber || "",
      zones: Array.isArray(row.zones) ? row.zones : [],
      musicServicePayment: row.musicServicePayment === 'SUSPENDED' ? 'SUSPENDED' : 'ACTIVE',
      paymentResolutionGuidance: row.paymentResolutionGuidance || null,
      canSelfRestoreMusicService: false,
    };
  };

  const [locations, setLocations] = useState([]);
  const [locationsLoading, setLocationsLoading] = useState(false);
  const [locationsError, setLocationsError] = useState(null);
  const [locationsUnavailableNote, setLocationsUnavailableNote] = useState(null);

  const reloadLocations = React.useCallback(async () => {
    if (!currentBrand.id) {
      setLocations([]);
      return;
    }
    setLocationsLoading(true);
    setLocationsError(null);
    try {
      const raw = await apiService.listMyLocations({ brandId: currentBrand.id });
      const data = raw?.data ?? raw;
      const rows = Array.isArray(data?.locations) ? data.locations : [];
      setLocations(rows.map(mapCanonicalLocation));
    } catch (err) {
      setLocations([]);
      setLocationsError(err?.message || 'Failed to load locations');
    } finally {
      setLocationsLoading(false);
    }
  }, [currentBrand.id]);

  // Load brand-scoped portfolio Locations from canonical API when Brand changes.
  React.useEffect(() => {
    let cancelled = false;

    if (currentBrand.id) {
      try {
        const brandViewModeKey = `locationsViewMode_brand_${currentBrand.id}`;
        const savedViewMode = localStorage.getItem(brandViewModeKey);
        setViewMode(savedViewMode || 'list');
        if (localStorage.getItem('locationsViewMode')) {
          localStorage.removeItem('locationsViewMode');
        }
      } catch (error) {
        console.error('Error loading brand-specific view mode:', error);
        setViewMode('list');
      }
    } else {
      setViewMode('list');
    }

    async function loadLocations() {
      if (!currentBrand.id) {
        setLocations([]);
        setLocationsError(null);
        setLocationsUnavailableNote('Select a Brand to view Locations this Master Account operates.');
        return;
      }
      setLocationsLoading(true);
      setLocationsError(null);
      setLocationsUnavailableNote(null);
      try {
        const raw = await apiService.listMyLocations({ brandId: currentBrand.id });
        const data = raw?.data ?? raw;
        const rows = Array.isArray(data?.locations) ? data.locations : [];
        if (!cancelled) {
          setLocations(rows.map(mapCanonicalLocation));
        }
      } catch (err) {
        console.error('Failed to load canonical locations', err);
        if (!cancelled) {
          setLocations([]);
          setLocationsError(err?.message || 'Failed to load locations');
        }
      } finally {
        if (!cancelled) setLocationsLoading(false);
      }
    }

    loadLocations();
    return () => {
      cancelled = true;
    };
  }, [currentBrand.name, currentBrand.id]);


  // Update the filter state structure
  const [filters, setFilters] = useState({
    filterBy: 'location', // default filter
    search: ''
  });

  // Add new state for view mode with brand-specific localStorage persistence
  const [viewMode, setViewMode] = useState(() => {
    // Always start with 'list' view for initial load or brand switching
    return 'list';
  });

  // Add state to track active tab
  const [activeTab, setActiveTab] = useState('zones-info');

  // Add state for search and channels
  const [searchQuery, setSearchQuery] = useState('');
  // Channel catalog is deferred — do not ship fabricated channel names/images.
  const [channels] = useState([]);


  // Update the placeholderMap (remove the 'name' entry)
  const placeholderMap = {
    location: 'Search by Location Name',
    address: 'Search by Address',
    city: 'Search by City',
    state: 'Search by State',
    timezone: 'Search by Time Zone',
    status: 'Search by Status'
  };

  // Simplify the getStateFromAddress function
  const getStateFromAddress = (address) => {
    // Simple pattern to match state code
    const stateRegex = /\b([A-Z]{2})\b/;
    const match = address.match(stateRegex);
    console.log('Address:', address, 'Extracted state:', match ? match[1] : null);
    return match ? match[1] : null;
  };

  // Also let's verify the coordinates for Colorado
  const STATE_COORDINATES = {
    'GA': { left: '72%', top: '62%' },  // Adjusted for Demorest, GA
    'CA': { left: '10%', top: '45%' },  // California - Coronado
    'CO': { left: '32%', top: '38%' },  // Further adjusted for Denver
    'AL': { left: '67%', top: '65%' },  // Alabama
    'AK': { left: '18%', top: '82%' },  // Alaska
    'AZ': { left: '25%', top: '60%' },  // Arizona
    'AR': { left: '58%', top: '60%' },  // Arkansas
    'CT': { left: '89%', top: '29%' },  // Connecticut
    'DE': { left: '87%', top: '39%' },  // Delaware
    'FL': { left: '77%', top: '80%' },  // Florida
    'HI': { left: '25%', top: '90%' },  // Hawaii
    'ID': { left: '22%', top: '28%' },  // Idaho
    'IL': { left: '60%', top: '42%' },  // Illinois
    'IN': { left: '67%', top: '40%' },  // Indiana
    'IA': { left: '54%', top: '35%' },  // Iowa
    'KS': { left: '48%', top: '50%' },  // Kansas
    'KY': { left: '70%', top: '48%' },  // Kentucky
    'LA': { left: '58%', top: '75%' },  // Louisiana
    'ME': { left: '92%', top: '15%' },  // Maine
    'MD': { left: '84%', top: '40%' },  // Maryland
    'MA': { left: '90%', top: '27%' },  // Massachusetts
    'MI': { left: '67%', top: '30%' },  // Michigan
    'MN': { left: '52%', top: '23%' },  // Minnesota
    'MS': { left: '62%', top: '70%' },  // Mississippi
    'MO': { left: '57%', top: '48%' },  // Missouri
    'MT': { left: '30%', top: '20%' },  // Montana
    'NE': { left: '45%', top: '40%' },  // Nebraska
    'NV': { left: '18%', top: '40%' },  // Nevada
    'NH': { left: '89%', top: '20%' },  // New Hampshire
    'NJ': { left: '87%', top: '35%' },  // New Jersey
    'NM': { left: '32%', top: '65%' },  // New Mexico
    'NY': { left: '83%', top: '25%' },  // New York
    'NC': { left: '80%', top: '52%' },  // North Carolina
    'ND': { left: '45%', top: '20%' },  // North Dakota
    'OH': { left: '73%', top: '40%' },  // Ohio
    'OK': { left: '48%', top: '58%' },  // Oklahoma
    'OR': { left: '15%', top: '25%' },  // Oregon
    'PA': { left: '82%', top: '35%' },  // Pennsylvania
    'RI': { left: '91%', top: '29%' },  // Rhode Island
    'SC': { left: '78%', top: '60%' },  // South Carolina
    'SD': { left: '45%', top: '30%' },  // South Dakota
    'TN': { left: '68%', top: '53%' },  // Tennessee
    'TX': { left: '45%', top: '70%' },  // Texas
    'UT': { left: '25%', top: '45%' },  // Utah
    'VT': { left: '87%', top: '20%' },  // Vermont
    'VA': { left: '80%', top: '45%' },  // Virginia
    'WA': { left: '17%', top: '15%' },  // Washington
    'WI': { left: '58%', top: '28%' },  // Wisconsin
    'WV': { left: '77%', top: '43%' },  // West Virginia
    'WY': { left: '32%', top: '35%' }   // Wyoming
  };

  const toggleRow = (id) => {
    setExpandedRows(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleRefresh = async () => {
    if (isRefreshing) return;
    setIsRefreshing(true);
    try {
      if (!currentBrand.id) {
        setLocations([]);
        return;
      }
      const raw = await apiService.listMyLocations({ brandId: currentBrand.id });
      const data = raw?.data ?? raw;
      const rows = Array.isArray(data?.locations) ? data.locations : [];
      setLocations(rows.map(mapCanonicalLocation));
      setLocationsError(null);
    } catch (err) {
      console.error('Refresh locations failed', err);
      setLocationsError(err?.message || 'Failed to refresh locations');
    } finally {
      setTimeout(() => setIsRefreshing(false), 400);
    }
  };

  const handleEditClick = (location) => {
    setEditingLocation(location);
    setEditableValues({
      name: location.name,
      address: location.address,
      timeZone: location.timeZone,
      region: location.region,
    });
    // Location edit mutations are deferred — modal remains for intended UX, read-only.
    setIsEditing(false);
    setHasChanges(false);
    setShowEditModal(true);
    setActiveTab('zones-info');
  };

  const handleOverlayClick = (e) => {
    if (e.target.className === 'modal-overlay') {
      setShowEditModal(false);
    }
  };

  const toggleEdit = () => {
    // Edit Location mutation is deferred — do not enter a fake editable workflow.
    window.alert('Edit Location is not available yet. Location updates require a future canonical write contract.');
    setIsEditing(false);
    setHasChanges(false);
  };

  const handleChange = (field, value) => {
    // Don't allow address changes since it's locked
    if (field === 'address') {
      return;
    }
    
    setEditableValues(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // Do not fabricate local success — Location edit writes are deferred.
    window.alert('Save is not available yet. Location edits are read-only until the canonical write contract ships.');
    setHasChanges(false);
    setIsEditing(false);
  };

  const handleRequestAddressChange = () => {
    // Navigate to support page with pre-filled message for address change request
    navigate(CLIENT_SUPPORT_HUB_PATH, { 
      state: { 
        prefillMessage: `I would like to request an address change for Location ${editingLocation?.id} (${editingLocation?.name}).\n\nCurrent Address: ${editingLocation?.address}\n\nPlease provide the new address details and reason for the change.` 
      } 
    });
  };

  // Add function to generate random 8-digit password
  // Update filter function (remove the 'name' case)
  const filteredLocations = locations.filter(location => {
    const searchTerm = filters.search.toLowerCase();
    if (!searchTerm) return true;

    switch (filters.filterBy) {
      case 'location':
        return location.name.toLowerCase().includes(searchTerm);
      case 'address':
        return location.address.toLowerCase().includes(searchTerm);
      case 'city':
        return location.address.toLowerCase().includes(searchTerm);
      case 'state':
        return location.address.toLowerCase().includes(searchTerm);
      case 'timezone':
        return location.timeZone.toLowerCase().includes(searchTerm);
      case 'status':
        return location.status.toLowerCase().includes(searchTerm);
      default:
        return true;
    }
  });

  // Pagination logic
  const totalItems = filteredLocations.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentLocations = filteredLocations.slice(startIndex, endIndex);

  // Reset to first page when search changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filters.search, filters.filterBy]);

  // Save view mode to localStorage when it changes (brand-specific)
  React.useEffect(() => {
    // Only save if we have a valid brand selected
    if (currentBrand.id && viewMode) {
      try {
        const brandViewModeKey = `locationsViewMode_brand_${currentBrand.id}`;
        localStorage.setItem(brandViewModeKey, viewMode);
        console.log(`Saved view mode for brand ${currentBrand.name}:`, viewMode);
      } catch (error) {
        console.error('Error saving brand-specific view mode:', error);
      }
    }
  }, [viewMode, currentBrand.id, currentBrand.name]);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Add filter function
  const filteredChannels = channels.filter(channel =>
    channel.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="locations-page-container">
        <h1 style={{ margin: '0', marginBottom: '0px', padding: '0' }}>Locations</h1>
      <div className="locations-content">
        <div className="locations-header">
            <div className="locations-total">
              Total Locations: {totalItems}
            </div>
            <select 
            className="filter-dropdown"
              value={filters.filterBy}
              onChange={(e) => setFilters(prev => ({ ...prev, filterBy: e.target.value }))}
            >
              <option value="location">Filter by Location</option>
            <option value="address">Address</option>
            <option value="city">City</option>
            <option value="state">State</option>
            <option value="timezone">Time Zone</option>
            <option value="status">Status</option>
            </select>
          
            <input
              type="text"
            placeholder={placeholderMap[filters.filterBy] || 'Search...'}
            className="search-input"
              value={filters.search}
              onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
            />
          
          <button 
            className="map-view-btn"
            onClick={() => setViewMode(viewMode === 'list' ? 'map' : 'list')}
          >
            {viewMode === 'list' ? 'Map View' : 'List View'}
          </button>

          {canCreateLocation ? (
            <button
              className="add-location-btn"
              type="button"
              onClick={() => setShowCreateModal(true)}
              data-testid="add-location-btn"
            >
              Add Location
            </button>
          ) : (
            <button
              className="add-location-btn"
              type="button"
              disabled
              title="Only Owner or Admin may create Locations"
              data-testid="add-location-btn"
            >
              Add Location
            </button>
          )}
        </div>

        {(locationsLoading || locationsError || locationsUnavailableNote) && (
          <div className="locations-data-status" style={{ margin: '8px 0 12px', color: locationsError ? '#b00020' : '#6c757d' }}>
            {locationsLoading && <span>Loading locations…</span>}
            {!locationsLoading && locationsError && <span>{locationsError}</span>}
            {!locationsLoading && !locationsError && locationsUnavailableNote && (
              <span>{locationsUnavailableNote}</span>
            )}
          </div>
        )}

      {viewMode === 'list' ? (
          <>
        <div className="locations-table">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Location Name</th>
                <th>Store #</th>
                <th>Address</th>
                <th>Time Zone</th>
                <th>Settings</th>
                <th>Status</th>
                <th>Music Service</th>
              </tr>
            </thead>
            <tbody>
              {currentLocations.map((location, index) => {
                // Presentation-only row ordinal for the "#" column (filtered + pagination-aware).
                // Do not expose internal locationId here.
                const rowNumber = startIndex + index + 1;
                return (
                      <React.Fragment key={location.id}>
                        <tr>
                    <td onClick={() => toggleRow(location.id)}>
                      <span className="location-row-id">
                        <span className="location-row-number">
                          {String(rowNumber).padStart(2, '0')}
                        </span>
                        <span
                          className={`dropdown-arrow ${expandedRows[location.id] ? 'expanded' : ''}`}
                          aria-hidden="true"
                        >
                          ▶
                        </span>
                      </span>
                    </td>
                    <td>{location.name}</td>
                    <td>{location.storeNumber || ""}</td>
                    <td>
                      {location.address.split('\n').map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </td>
                    <td>{location.timeZone}</td>
                    <td>
                      <button 
                        className="edit-btn" 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditClick(location);
                        }}
                      >
                        Edit
                      </button>
                    </td>
                    <td>
                      <div className={`status-badge ${location.status.toLowerCase()}`}>
                        {location.status === 'Good' ? (
                          <>
                            <span className="status-text">Good</span>
                            <img src={kkFavicon} alt="Good" className="status-icon" />
                          </>
                        ) : (
                          <>
                            <span className="status-text">Check</span>
                            <img src={checkWarning} alt="Check" className="status-icon" />
                          </>
                        )}
                      </div>
                      <div className="last-played">{location.lastPlayed}</div>
                    </td>
                    <td>
                      <div data-testid="music-service-payment">
                        {location.musicServicePayment || 'ACTIVE'}
                      </div>
                      {location.musicServicePayment === 'SUSPENDED' && location.paymentResolutionGuidance ? (
                        <div className="text-muted" style={{ fontSize: 12, maxWidth: 220 }}>
                          {location.paymentResolutionGuidance}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                  {expandedRows[location.id] && (
                    <tr className="expanded-row">
                      <td className="locations-expanded-cell" colSpan={8}>
                        <div className="zones-info">
                          <table className="zones-table">
                            <thead>
                              <tr>
                                <th>
                                  <button
                                    type="button"
                                    className="refresh-zones-btn"
                                    onClick={handleRefresh}
                                    aria-label="Refresh Zones"
                                  >
                                    <span
                                      className={`refresh-icon ${isRefreshing ? 'refreshing' : ''}`}
                                      aria-hidden="true"
                                    >
                                      ↻
                                    </span>
                                  </button>
                                </th>
                                <th>Zones</th>
                                <th>Music Service</th>
                                <th>Channel</th>
                                <th>Song</th>
                                <th>Message</th>
                                <th>Last Played</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(location.zones || []).length === 0 ? (
                                <tr>
                                  <td colSpan={7} style={{ textAlign: 'left', padding: '12px 16px', color: '#6c757d' }}>
                                    No zones are recorded for this location.
                                  </td>
                                </tr>
                              ) : (
                                location.zones.map((zone) => (
                                  <tr key={zone.zoneId || zone.name}>
                                    <td />
                                    <td>{zone.name || ""}</td>
                                    <td>{zone.musicServicePayment === 'SUSPENDED' ? 'SUSPENDED' : 'ACTIVE'}</td>
                                    <td />
                                    <td />
                                    <td />
                                    <td />
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                          <button className="add-zone-btn" type="button" disabled title="Zone management requires the Zones control-plane (not in this phase).">Add Zone</button>
                        </div>
                      </td>
                    </tr>
                  )}
                      </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        
        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div className="pagination-container">
            <div className="pagination-info">
              Showing {startIndex + 1} to {Math.min(endIndex, totalItems)} locations
            </div>
            <div className="pagination-controls">
              <button 
                className="pagination-btn"
                onClick={goToPreviousPage}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                <button
                  key={page}
                  className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  onClick={() => goToPage(page)}
                >
                  {page}
                </button>
              ))}
              
              <button 
                className="pagination-btn"
                onClick={goToNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </button>
            </div>
          </div>
        )}
                      </>
                    ) : (
          <div className="map-view-wrapper">
            <USMap 
              locations={locations} 
              onPinClick={(location) => {
                console.log('Pin clicked:', location);
                // Handle pin click
                handleEditClick(location);
              }}
            />
          </div>
        )}
        </div>
        {showEditModal && (
          <div 
            className="modal-overlay"
            onClick={handleOverlayClick}
          >
            <div className="modal-content">
              <div className="modal-header">
                <div className="header-content">
                  <h2>
                    <img src={locationsIcon} alt="Location" className="header-icon" />
                    Location {editingLocation?.id}
                    <div className="header-actions">
                      {isEditing && hasChanges && (
                        <button 
                          className="save-btn"
                          onClick={handleSave}
                        >
                          Save
                        </button>
                      )}
                      <img 
                        src={isEditing ? closeIcon : editIcon} 
                        alt={isEditing ? "Close" : "Edit"} 
                        className="edit-icon"
                        onClick={toggleEdit}
                      />
                    </div>
                  </h2>
                  <div className="location-details">
                    <div className="detail-item">
                      <img src={appIcon} alt="Building" className="detail-icon" />
                      {isEditing ? (
                        <input 
                          type="text" 
                          value={editableValues.name}
                          onChange={(e) => handleChange('name', e.target.value)}
                          className="editable-input"
                        />
                      ) : (
                        <span>{editingLocation?.name}</span>
                      )}
                    </div>
                    <div className="detail-item">
                      <img src={appIcon} alt="Location" className="detail-icon" />
                      <div className="address-field-container">
                        <span className="locked-address">{editingLocation?.address}</span>
                        <div className="address-lock-info">
                          <img src={infoIcon} alt="Info" className="info-icon" />
                          <div className="info-tooltip">
                            <p>Location addresses are locked for security and compliance reasons. Address changes must be reviewed and approved by our Super Admin team.</p>
                            <button 
                              className="request-change-link"
                              onClick={handleRequestAddressChange}
                            >
                              Request an address change
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="detail-item">
                      <img src={appIcon} alt="Clock" className="detail-icon" />
                      {isEditing ? (
                        <>
                          <input 
                            type="text" 
                            value={editableValues.timeZone}
                            onChange={(e) => handleChange('timeZone', e.target.value)}
                            className="editable-input"
                          />
                          <span className="region-separator">•</span>
                          <input 
                            type="text" 
                            value={editableValues.region || ''}
                            onChange={(e) => handleChange('region', e.target.value)}
                            className="editable-input"
                          />
                        </>
                      ) : (
                        <span>
                          {editingLocation?.timeZone}
                          <span className="region-separator">•</span>
                          {editingLocation?.region}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <button 
                  className="close-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowEditModal(false);
                  }}
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <div className="modal-sidebar">
                  <div 
                    className={`sidebar-item ${activeTab === 'zones-info' ? 'active' : ''}`}
                    onClick={() => setActiveTab('zones-info')}
                  >
                    <img src={appIcon} alt="Zones Info" className="app-icon" />
                    <span>Zones Info</span>
                  </div>
                  <div 
                    className={`sidebar-item ${activeTab === 'zones-music' ? 'active' : ''}`}
                    onClick={() => setActiveTab('zones-music')}
                  >
                    <img src={assistantIcon} alt="Zones Music" className="app-icon" />
                    <span>Zones Music</span>
                  </div>
                  <div 
                    className={`sidebar-item ${activeTab === 'zones-messaging' ? 'active' : ''}`}
                    onClick={() => setActiveTab('zones-messaging')}
                  >
                    <img src={osIcon} alt="Zones Messaging" className="app-icon" />
                    <span>Zones Messaging</span>
                  </div>
                </div>
                <div className="modal-main-content">
                  {activeTab === 'zones-info' ? (
                    <div className="app-section">
                      <h3>Zones Info</h3>
                      <div className="zones-table-container">
                        <p style={{ color: '#6c757d', margin: '12px 0' }}>
                          Zone and device details are not available yet. Canonical Zone/Device services are not connected in this phase.
                        </p>
                        <div className="add-zone-row">
                          <button className="add-zone-btn" type="button" disabled title="Zone management requires the Zones control-plane (not in this phase).">Add Zone</button>
                        </div>
                      </div>
                    </div>
                  ) : activeTab === 'zones-music' ? (
                    <div className="zones-music-section">
                      <div className="section-heading">Zones Music</div>
                      <p style={{ color: '#6c757d', margin: '12px 0' }}>
                        Channel assignment is not available yet. No fabricated channel catalog is shown.
                      </p>
                      <div className="channel-selection">
                        <div className="search-box">
                          <input
                            type="text"
                            placeholder="Search"
                            className="channel-search"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            disabled
                            title="Channel search requires the Channels control-plane."
                          />
                        </div>
                        <div className="channel-grid">
                          {filteredChannels.length === 0 && (
                            <div className="channel-item" style={{ color: '#6c757d' }}>No channels available.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="zones-messaging-section app-section">
                      <h3>Zones Messaging</h3>
                      <p style={{ color: '#6c757d', margin: '12px 0' }}>
                        Messaging schedule controls are not available yet. Canonical messaging services are not connected in this phase.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
        <LocationCreateModal
          open={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          brandId={currentBrand?.id || ''}
          brandName={currentBrand?.name || ''}
          onCreated={() => {
            reloadLocations();
          }}
        />
    </div>
  );
};

export default Locations; 